import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useProgress } from "../../hooks/useData";
import { Kpi, PageHeader, Panel, SimBadge } from "./CareLayout";
import { PatientPicker, usePickedPatient } from "./PatientPicker";

const C = { teal: "#17A2A2", dark: "#0A2540", gold: "#E0B33A", green: "#10B981", slate: "#94A3B8" };

const Chart = ({ title, data, lines, bar, domain, testId }) => (
  <Panel title={title} testId={testId}>
    <div className="h-56">
      <ResponsiveContainer>
        {bar ? (
          <BarChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="#94A3B8" strokeOpacity={0.28} /><XAxis dataKey="label" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} domain={domain} /><Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
            {lines.map(([k, n, c]) => <Bar key={k} dataKey={k} name={n} fill={c} radius={[6, 6, 0, 0]} />)}</BarChart>
        ) : (
          <LineChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="#94A3B8" strokeOpacity={0.28} /><XAxis dataKey="label" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} domain={domain} /><Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
            {lines.map(([k, n, c]) => <Line key={k} type="monotone" dataKey={k} name={n} stroke={c} strokeWidth={3} dot={{ r: 4 }} />)}</LineChart>
        )}
      </ResponsiveContainer>
    </div>
  </Panel>
);

const ROWS = [
  ["rom_shoulder_flexion_max", "Flexion d'épaule max (°)"], ["rom_shoulder_abduction_max", "Abduction max (°)"], ["functional_reach_max_cm", "Atteinte max (cm)"],
  ["accuracy_mean", "Précision (%)"], ["reaction_time_mean_ms", "Temps de réaction (ms)"], ["repetitions", "Répétitions"],
  ["affected_side_use_pct", "Côté atteint (%)"], ["symmetry_pct", "Symétrie G/D (%)"], ["completion_pct", "Complétion (%)"],
];

export default function Progress() {
  const pp = usePickedPatient();
  const { data } = useProgress(pp.pid);
  const s = data?.series || [];
  const cur = data?.current;
  return (
    <div data-testid="care-progress">
      <PageHeader title="Progrès" sub="Évolution des indicateurs de rééducation"><SimBadge /><PatientPicker {...pp} /></PageHeader>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Séances analysées" value={s.length} testId="progress-kpi-sessions" />
        <Kpi label="Assiduité (14 j)" value={data?.adherence_pct} unit="%" hint={`${data?.sessions_last_14_days ?? 0}/${data?.expected_last_14_days ?? 0} séances prescrites`} testId="progress-kpi-adherence" />
        <Kpi label="Flexion max actuelle" value={cur?.rom_shoulder_flexion_max} unit="°" testId="progress-kpi-rom" />
        <Kpi label="Précision actuelle" value={cur?.accuracy_mean} unit="%" testId="progress-kpi-accuracy" />
      </div>
      <Panel title="Comparaison : première · précédente · actuelle" className="mt-6" testId="progress-comparison">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead><tr className="text-left text-xs uppercase text-muted-foreground/80"><th className="pb-2">Indicateur</th><th>Première séance</th><th>Séance précédente</th><th>Séance actuelle</th></tr></thead>
            <tbody>{ROWS.map(([k, l]) => (
              <tr key={k} className="border-t border-border/60"><td className="py-2">{l}</td><td className="font-mono">{data?.first?.[k] ?? "—"}</td><td className="font-mono">{data?.previous?.[k] ?? "—"}</td><td className="font-mono font-bold text-primary-strong">{cur?.[k] ?? "—"}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </Panel>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Chart testId="chart-rom" title="Amplitude articulaire (côté atteint)" data={s} lines={[["rom_shoulder_flexion_max", "Flexion max (°)", C.teal], ["rom_shoulder_abduction_max", "Abduction max (°)", C.gold]]} />
        <Chart testId="chart-reach" title="Atteinte fonctionnelle (cm)" data={s} lines={[["functional_reach_max_cm", "Atteinte max (cm)", C.dark]]} />
        <Chart testId="chart-accuracy" title="Précision du mouvement (%)" data={s} domain={[0, 100]} lines={[["accuracy_mean", "Précision (%)", C.green]]} />
        <Chart testId="chart-reaction" title="Temps de réaction (ms)" data={s} lines={[["reaction_time_mean_ms", "Temps de réaction (ms)", C.gold], ["movement_time_mean_ms", "Temps de mouvement (ms)", C.slate]]} />
        <Chart testId="chart-reps" title="Répétitions" data={s} bar lines={[["successful_repetitions", "Réussies", C.teal], ["repetitions", "Total", C.slate]]} />
        <Chart testId="chart-symmetry" title="Côté atteint & symétrie gauche/droite (%)" data={s} domain={[0, 100]} lines={[["affected_side_use_pct", "Utilisation côté atteint", C.teal], ["symmetry_pct", "Symétrie G/D", C.dark]]} />
        <Chart testId="chart-completion" title="Complétion des séances (%)" data={s} bar domain={[0, 100]} lines={[["completion_pct", "Complétion", C.green]]} />
      </div>
    </div>
  );
}
